import { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow,
  TableCell, TableBody, TextField, Select, MenuItem, FormControl,
  Button, CircularProgress, Divider, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import authFetch, { responseError } from '../utils/authFetch';
import { NAVY, inputSx, tbl, typo } from '../styles/ds';

const CURRENCY = [{ id: 'ILS', label: '₪' }, { id: 'USD', label: '$' }];

// ── Section 1: Matrix — product × role costs ──────────────────────────────────
const MatrixRates = () => {
  const notify  = useNotify();
  const [data,    setData]    = useState(null); // { products, roles, costs }
  const [edited,  setEdited]  = useState({});   // { product_id: { role_name: { cost_ils, cost_usd, currency } } }
  const [saving,  setSaving]  = useState(null);

  useEffect(() => {
    authFetch('/product_role_costs')
      .then(r => r.json())
      .then(d => { setData(d); setEdited(JSON.parse(JSON.stringify(d.costs || {}))); })
      .catch(() => {});
  }, []);

  const setCell = (productId, roleName, field, value) => {
    setEdited(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [roleName]: {
          cost_ils: 0, cost_usd: 0, currency: 'ILS',
          ...(prev[productId]?.[roleName] || {}),
          [field]: value,
        },
      },
    }));
  };

  const saveRow = async (product) => {
    setSaving(product.id);
    const rowCosts = edited[product.id] || {};
    const payload  = Object.entries(rowCosts).map(([role_name, vals]) => ({
      role_name,
      cost_ils: Number(vals.cost_ils) || 0,
      cost_usd: Number(vals.cost_usd) || 0,
      currency: vals.currency || 'ILS',
    }));
    try {
      const res = await authFetch(`/product_role_costs/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ costs: payload }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בשמירה'), { type: 'error' }); return; }
      notify(`תעריפי "${product.name}" נשמרו`, { type: 'success' });
    } catch { notify('שגיאה בשמירה', { type: 'error' }); }
    finally { setSaving(null); }
  };

  if (!data) return <CircularProgress size={24} />;
  const { products, roles } = data;

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        <Box data-mobile-scroll sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: NAVY }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, minWidth: 140 }}>קטגוריה</TableCell>
                {roles.map(role => (
                  <TableCell key={role} align="center"
                    sx={{ color: '#fff', fontWeight: 700, minWidth: 130, whiteSpace: 'nowrap' }}>
                    {role}
                  </TableCell>
                ))}
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">שמור</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product, pi) => {
                const rowData = edited[product.id] || {};
                return (
                  <TableRow key={product.id} sx={tbl.zebraRow(pi)}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13, color: NAVY, whiteSpace: 'nowrap' }}>
                      {product.name}
                    </TableCell>
                    {roles.map(role => {
                      const cell = rowData[role] || { cost_ils: 0, cost_usd: 0, currency: 'ILS' };
                      const hasValue = Number(cell.cost_ils) > 0 || Number(cell.cost_usd) > 0;
                      return (
                        <TableCell key={role} align="center" sx={{ py: 0.8, px: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <Typography fontSize={11} color="text.secondary">₪</Typography>
                              <TextField
                                size="small" type="number"
                                value={cell.cost_ils ?? 0}
                                onChange={e => setCell(product.id, role, 'cost_ils', e.target.value)}
                                inputProps={{ min: 0, step: '0.5', style: { textAlign: 'center', width: 60 } }}
                                sx={{ '& .MuiInputBase-root': { height: 28, fontSize: 12 },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: hasValue ? '#1565c0' : 'rgba(0,0,0,0.2)' } }}
                              />
                            </Box>
                            {hasValue && (
                              <Chip label={`₪${Number(cell.cost_ils).toLocaleString()}`} size="small"
                                sx={{ fontSize: 10, height: 16, backgroundColor: 'rgba(21,101,192,0.1)', color: '#1565c0' }} />
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Button size="small" variant="contained" onClick={() => saveRow(product)}
                        disabled={saving === product.id}
                        startIcon={saving === product.id
                          ? <CircularProgress size={12} color="inherit" />
                          : <SaveIcon sx={{ fontSize: 13 }} />}
                        sx={{ backgroundColor: NAVY, borderRadius: 1.5, px: 1.2, fontSize: 11,
                          '&:hover': { backgroundColor: '#162d5a' } }}>
                        שמור
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Box px={2} py={1}>
          <Typography fontSize={11} color="text.secondary">
            * הזן 0 כדי לא לכלול סוג עבודה עבור קטגוריה מסוימת. מחיר ₪ בלבד מספיק לרוב המקרים.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Section 2: Per-category overall work cost ─────────────────────────────────
const CategoryWorkCosts = () => {
  const notify  = useNotify();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null);

  useEffect(() => {
    authFetch('/product_work_costs')
      .then(r => r.json())
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const setField = (productId, field, value) =>
    setItems(rs => rs.map(r => r.product_id === productId ? { ...r, [field]: value } : r));

  const saveItem = async (item) => {
    setSaving(item.product_id);
    try {
      const res = await authFetch(`/product_work_costs/${item.product_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cost_ils:    Number(item.cost_ils)  || 0,
          cost_usd:    Number(item.cost_usd)  || 0,
          currency:    item.currency || 'ILS',
          description: item.description || '',
        }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בשמירה'), { type: 'error' }); return; }
      notify(`עלות כוללת של "${item.product_name}" עודכנה`, { type: 'success' });
    } catch { notify('שגיאה בשמירה', { type: 'error' }); }
    finally { setSaving(null); }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        <Box data-mobile-scroll sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: NAVY }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>קטגוריה</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">עלות כוללת ₪</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">עלות כוללת $</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">מטבע</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>הערה</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">שמור</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, i) => (
                <TableRow key={item.product_id} sx={tbl.zebraRow(i)}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 13, color: NAVY }}>{item.product_name}</TableCell>
                  <TableCell align="center">
                    <TextField size="small" type="number" value={item.cost_ils ?? 0}
                      onChange={e => setField(item.product_id, 'cost_ils', e.target.value)}
                      inputProps={{ min: 0, step: '0.5', style: { textAlign: 'center' } }}
                      sx={{ ...inputSx, width: 110 }}
                      InputProps={{ startAdornment: <Typography fontSize={12} color="text.secondary" mr={0.5}>₪</Typography> }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField size="small" type="number" value={item.cost_usd ?? 0}
                      onChange={e => setField(item.product_id, 'cost_usd', e.target.value)}
                      inputProps={{ min: 0, step: '0.5', style: { textAlign: 'center' } }}
                      sx={{ ...inputSx, width: 110 }}
                      InputProps={{ startAdornment: <Typography fontSize={12} color="text.secondary" mr={0.5}>$</Typography> }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControl size="small">
                      <Select value={item.currency || 'ILS'}
                        onChange={e => setField(item.product_id, 'currency', e.target.value)}
                        sx={{ ...inputSx, width: 80 }}>
                        {CURRENCY.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={item.description || ''}
                      onChange={e => setField(item.product_id, 'description', e.target.value)}
                      placeholder="למשל: כתיבה + בדיקה"
                      inputProps={{ dir: 'rtl' }}
                      sx={{ ...inputSx, minWidth: 160 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="contained" onClick={() => saveItem(item)}
                      disabled={saving === item.product_id}
                      startIcon={saving === item.product_id ? <CircularProgress size={13} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
                      sx={{ backgroundColor: NAVY, borderRadius: 1.5, px: 1.5, fontSize: 12, '&:hover': { backgroundColor: '#162d5a' } }}>
                      שמור
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function WorkRates() {
  return (
    <Box className="crm-page" p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Typography {...typo.pageTitle} mb={1}>תעריפי עבודה</Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        הגדר עלויות ברירת מחדל — יתמלאו אוטומטית במנות ייצור.
      </Typography>

      <Typography variant="h6" fontWeight={700} color={NAVY} mb={0.5}>
        תעריף לפי קטגוריה × סוג עבודה
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        עלות כל סוג עבודה משתנה לפי הקטגוריה — הגדר את העלות בכל תא.
      </Typography>
      <MatrixRates />

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" fontWeight={700} color={NAVY} mb={0.5}>
        עלות עבודה כוללת לפי קטגוריה
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        עלות עבודה אחת כוללת לכל הקטגוריה — לדוגמא כל עבודת הכתיבה על פרשיות.
      </Typography>
      <CategoryWorkCosts />
    </Box>
  );
}
